import { useEffect, useState, type ComponentProps } from 'react';
import { InputField } from '@/kit/FormField';

type InputProps = Omit<ComponentProps<typeof InputField>, 'value' | 'onChange' | 'type' | 'charMode'>;

interface NumberBoxProps extends InputProps {
  /** The number held by the screen; null or undefined shows an empty box. */
  value: number | null | undefined;
  /** Called with the typed number (null when the box is emptied). */
  onValue: (value: number | null) => void;
  /** The largest value that can be typed; a keystroke that would pass it is ignored (100 for a percentage). */
  max?: number;
  /** Digits allowed before the decimal point (15 fits DECIMAL(19,4)). */
  maxIntDigits?: number;
  /** Digits allowed after the decimal point; 0 makes it a whole-number box. */
  maxDecimals?: number;
}

const toText = (value: number | null | undefined) => (value === null || value === undefined ? '' : `${value}`);

/**
 * A number box that is a text box underneath: only digits and one decimal point can be typed (no minus sign and no `e`,
 * which `type="number"` lets through), and a value above `max` is refused as it is typed. The screen keeps the number;
 * the box keeps what was typed, so "12." is not turned into "12" while the decimal point is being typed.
 */
export function NumberBox({ value, onValue, max, maxIntDigits = 15, maxDecimals = 4, ...rest }: NumberBoxProps) {
  const [text, setText] = useState(() => toText(value));

  // A value that changed from outside (a reload, Discard) replaces the text; the user's own typing does not.
  useEffect(() => {
    setText((current) => {
      const typed = current === '' || current === '.' ? null : Number(current);
      return typed === (value ?? null) ? current : toText(value);
    });
  }, [value]);

  return (
    <InputField
      {...rest}
      inputMode="decimal"
      charMode="number"
      allowDecimal={maxDecimals > 0}
      maxIntDigits={maxIntDigits}
      maxDecimals={maxDecimals}
      value={text}
      onChange={(event) => {
        const next = event.target.value;
        const parsed = next === '' || next === '.' ? null : Number(next);
        if (parsed !== null && (!Number.isFinite(parsed) || (max !== undefined && parsed > max))) return;
        setText(next);
        onValue(parsed);
      }}
    />
  );
}
